'use client';

import { useCallback, useMemo } from 'react';
import type { useFormatter, useTranslations } from 'next-intl';
import type { AuthUser } from '@/contexts/AuthContext';
import type { Project as ProjectSummary } from '@/types/project';

interface UseDashboardUtilitiesOptions {
  /** The currently-resolved auth user (drives the profile display name). */
  user: AuthUser | null;
  /** The `common.time` translator (next-intl `useTranslations('common.time')`). */
  tTime: ReturnType<typeof useTranslations>;
  /** The next-intl formatter (`useFormatter()`), used for locale-aware long dates. */
  formatter: ReturnType<typeof useFormatter>;
}

interface UseDashboardUtilitiesResult {
  formatTime: (dateString: string | null) => string;
  getProjectThumbnailSrc: (project: ProjectSummary) => string | null;
  getProjectActivityDate: (project: ProjectSummary) => string | null;
  profileInitial: string;
  profileDisplayName: string;
}

/**
 * `useDashboardUtilities` — the pure presentation helpers that the dashboard
 * provider exposes for project cards and the profile avatar:
 *
 *   • `formatTime` — relative-time bucketing (justNow / minutes / hours / days)
 *     with a locale-aware long-date fallback past 30 days, plus naive-timestamp
 *     timezone normalization (`Z` appended when no offset is present).
 *   • `getProjectActivityDate` — date precedence
 *     `updatedAt > lastMessageAt > lastActiveAt > createdAt`.
 *   • `getProjectThumbnailSrc` — null-guarded thumbnail URL with an activity-keyed
 *     `?v=` cache-buster (closes over `getProjectActivityDate`, so the two are
 *     kept paired here).
 *   • `profileDisplayName` / `profileInitial` — `name > local-part-of-email >
 *     email > 'User'` fallback chain.
 *
 * All bodies are byte-identical to the originals in `DashboardContext`; the only
 * change is that the closed-over `user`, `tTime`, and `formatter` are injected as
 * params rather than captured from the provider scope.
 */
export function useDashboardUtilities({
  user,
  tTime,
  formatter,
}: UseDashboardUtilitiesOptions): UseDashboardUtilitiesResult {
  const getProjectActivityDate = useCallback((project: ProjectSummary): string | null => {
    return project.updatedAt ?? project.lastMessageAt ?? project.lastActiveAt ?? project.createdAt ?? null;
  }, []);

  const formatTime = useCallback(
    (dateString: string | null): string => {
      if (!dateString) return tTime('justNow');

      let utcDateString = dateString;
      const hasTimezone =
        dateString.endsWith('Z') || dateString.includes('+') || dateString.match(/[-+]\d{2}:\d{2}$/) !== null;

      if (!hasTimezone) {
        utcDateString = dateString + 'Z';
      }

      const date = new Date(utcDateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return tTime('justNow');
      if (diffMins < 60) return tTime('shortMinutes', { count: diffMins });
      if (diffHours < 24) return tTime('shortHours', { count: diffHours });
      if (diffDays < 30) return tTime('shortDays', { count: diffDays });

      // Locale-aware long date via next-intl formatter (respects active locale).
      return formatter.dateTime(date, {
        month: 'short',
        day: 'numeric',
        ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
      });
    },
    [formatter, tTime],
  );

  const getProjectThumbnailSrc = useCallback(
    (project: ProjectSummary): string | null => {
      if (!project.thumbnailUrl) return null;
      const thumbnailVersion = getProjectActivityDate(project) ?? project.createdAt;
      return `${project.thumbnailUrl}${project.thumbnailUrl.includes('?') ? '&' : '?'}v=${encodeURIComponent(
        thumbnailVersion ?? '',
      )}`;
    },
    [getProjectActivityDate],
  );

  const profileDisplayName = useMemo(() => {
    const name = user?.name?.trim();
    if (name) return name;
    const email = user?.email?.trim();
    if (!email) return 'User';
    const localPart = email.split('@')[0]?.trim();
    return localPart || email;
  }, [user?.email, user?.name]);

  const profileInitial = profileDisplayName.charAt(0).toUpperCase();

  return {
    formatTime,
    getProjectThumbnailSrc,
    getProjectActivityDate,
    profileInitial,
    profileDisplayName,
  };
}
